import type { AddAsVersionMode } from "@/stores/documents/repository";

/** An existing document staged in the "Add new version" dialog. */
export interface StagedExistingDocument {
  id: string;
  filename: string;
  version: number | null;
  /** Null for a standalone document (its own lineage of one). */
  versionGroup: string | null;
  projectId: number | null;
  /** Only the current version of a lineage may be moved — a move takes the
   *  whole history with it. */
  isCurrentVersion: boolean;
}

/**
 * One row of the dialog's ordered list. The list IS the version assignment:
 * rows are committed top to bottom and the last one becomes current.
 */
export type StagedVersionItem =
  | { kind: "upload"; key: string; file: File }
  | {
      kind: "existing";
      key: string;
      mode: AddAsVersionMode;
      document: StagedExistingDocument;
      /**
       * What moving this lineage appends: every row (trashed ones take a
       * number too) and the live ones the history will show. Null while being
       * read; a move of a standalone document is 1 / 1.
       */
      lineage: { rowCount: number; liveCount: number } | null;
      /** The lineage could not be read; the row can't be committed as a move. */
      lineageFailed?: boolean;
    };

/** How many version numbers a row takes. */
export function versionSpan(item: StagedVersionItem): number {
  if (item.kind === "existing" && item.mode === "move") return item.lineage?.rowCount ?? 1;
  return 1;
}

/** How many versions a row adds to what the history shows. */
export function liveVersionSpan(item: StagedVersionItem): number {
  if (item.kind === "existing" && item.mode === "move") return item.lineage?.liveCount ?? 1;
  return 1;
}

/** A display name for the row, used by sort-by-name and the toasts. */
export function stagedItemName(item: StagedVersionItem): string {
  return item.kind === "upload" ? item.file.name : item.document.filename;
}

/** The document new versions are being added to, as the dialog needs it. */
export interface AddVersionsTarget {
  id: string;
  filename: string;
  version: number | null;
  versionGroup: string | null;
  projectId: number | null;
  parentDocumentId: string | null;
}

/**
 * What a host reports back after committing the staged list. `committed` is how
 * many LEADING items landed — the dialog drops exactly those and keeps the rest
 * staged, so a failure part-way leaves the user one retry away.
 */
export interface AddVersionsOutcome {
  committed: number;
  touchedVersionGroups: string[];
}

/** React Query key for a lineage's version figures (`fetchLineageVersionStats`). */
export function lineageStatsKey(versionGroup: string): readonly [string, string] {
  return ["lineage-version-stats", versionGroup];
}

/** React Query key for the Versioning dialog's list of live versions. */
export function versionHistoryKey(versionGroup: string): readonly [string, string] {
  return ["version-history", versionGroup];
}

/** React Query key for whether the caller may reorder a lineage (`fetchLineageEditable`). */
export function lineageEditableKey(versionGroup: string): readonly [string, string] {
  return ["version-lineage-editable", versionGroup];
}

/** React Query key for whether the caller may detach from a lineage (`fetchLineageDetachable`). */
export function lineageDetachableKey(versionGroup: string): readonly [string, string] {
  return ["version-lineage-detachable", versionGroup];
}

/** React Query key for the Versioning dialog's lineage rows, trashed included. */
export function lineageRowsKey(versionGroup: string): readonly [string, string] {
  return ["version-lineage-rows", versionGroup];
}
